import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronDown, ChevronUp, ImagePlus, LayoutTemplate, X } from 'lucide-react';
import { useI18n } from '../../hooks/useI18n';
import { classNames } from '../../utils/classNames';
import {
  getReferenceImageKey,
  REFERENCE_IMAGE_MAX_COUNT,
  REFERENCE_IMAGE_MAX_SIZE_BYTES,
  REFERENCE_IMAGE_MAX_SIZE_MB,
  validateReferenceImage
} from '../../utils/referenceImages';

export const AiReferenceImagesField = ({
  value = [],
  onChange,
  maxCount = REFERENCE_IMAGE_MAX_COUNT,
  maxSizeBytes = REFERENCE_IMAGE_MAX_SIZE_BYTES,
  disabled = false,
  error = ''
}) => {
  const { t } = useI18n();
  const fileInputRef = useRef(null);
  const previewUrlsRef = useRef(new Map());
  const [dragOver, setDragOver] = useState(false);
  const [fieldError, setFieldError] = useState('');
  const [expanded, setExpanded] = useState(false);
  const [, setPreviewVersion] = useState(0);

  useEffect(() => {
    setExpanded(value.length > 0);
  }, [value.length]);

  const refreshPreviews = useCallback(() => {
    const activeKeys = new Set(value.map(getReferenceImageKey));

    previewUrlsRef.current.forEach((url, key) => {
      if (!activeKeys.has(key)) {
        URL.revokeObjectURL(url);
        previewUrlsRef.current.delete(key);
      }
    });

    value.forEach((file) => {
      const key = getReferenceImageKey(file);

      if (!previewUrlsRef.current.has(key)) {
        previewUrlsRef.current.set(key, URL.createObjectURL(file));
      }
    });

    setPreviewVersion((current) => current + 1);
  }, [value]);

  useEffect(() => {
    refreshPreviews();
  }, [refreshPreviews]);

  useEffect(
    () => () => {
      previewUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
      previewUrlsRef.current.clear();
    },
    []
  );

  const addFiles = useCallback(
    (fileList) => {
      if (disabled || !fileList?.length) {
        return null;
      }

      const remainingSlots = maxCount - value.length;

      if (remainingSlots <= 0) {
        setFieldError(t('aiReferenceImagesMax', { max: maxCount }));
        return 'aiReferenceImagesMax';
      }

      const nextFiles = [...value];
      let nextErrorKey = null;

      Array.from(fileList)
        .slice(0, remainingSlots)
        .forEach((file) => {
          const validation = validateReferenceImage(file, maxSizeBytes);

          if (!validation.ok) {
            nextErrorKey = validation.errorKey;
            return;
          }

          const duplicate = nextFiles.some((item) => getReferenceImageKey(item) === getReferenceImageKey(file));

          if (!duplicate) {
            nextFiles.push(file);
          }
        });

      if (nextFiles.length !== value.length) {
        setFieldError('');
        setExpanded(true);
        onChange?.(nextFiles);
      }

      if (fileList.length > remainingSlots && !nextErrorKey) {
        setFieldError(t('aiReferenceImagesMax', { max: maxCount }));
        return 'aiReferenceImagesMax';
      }

      if (nextErrorKey) {
        setFieldError(t(nextErrorKey));
      }

      return nextErrorKey;
    },
    [disabled, maxCount, maxSizeBytes, onChange, t, value]
  );

  const handleFileChange = (event) => {
    if (disabled) {
      return;
    }

    addFiles(event.target.files);
    event.target.value = '';
  };

  const openFilePicker = () => {
    if (disabled || value.length >= maxCount) {
      return;
    }

    setExpanded(true);
    fileInputRef.current?.click();
  };

  const removeFile = (index) => {
    if (disabled) {
      return;
    }

    onChange?.(value.filter((_, itemIndex) => itemIndex !== index));
  };

  const handleDragOver = (event) => {
    event.preventDefault();

    if (disabled || value.length >= maxCount) {
      return;
    }

    setDragOver(true);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setDragOver(false);

    if (disabled) {
      return;
    }

    setExpanded(true);
    addFiles(event.dataTransfer?.files);
  };

  const canAddMore = value.length < maxCount;
  const hasImages = value.length > 0;

  if (!expanded) {
    return (
      <div className="space-y-1">
        <button
          type="button"
          disabled={disabled}
          onClick={() => setExpanded(true)}
          className="flex w-full items-center gap-2 rounded-xl border border-dashed border-border/80 bg-white/30 px-3 py-2 text-start transition hover:border-accent/35 hover:bg-white/55 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-slate-950/10 dark:hover:bg-slate-950/25"
        >
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg border border-border/70 bg-accent-soft/80 text-accent">
            <LayoutTemplate size={14} />
          </span>
          <span className="min-w-0 flex-1 text-xs leading-5 text-muted">{t('aiReferenceImagesCollapsedHint')}</span>
          <ChevronDown size={14} className="shrink-0 text-muted" aria-hidden />
        </button>
        {(error || fieldError) ? (
          <p className="text-xs text-rose-600 dark:text-rose-300" role="alert">
            {error || fieldError}
          </p>
        ) : null}
      </div>
    );
  }

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-medium text-muted">{t('aiReferenceImagesLabel')}</span>
        {!hasImages ? (
          <button
            type="button"
            disabled={disabled}
            onClick={() => setExpanded(false)}
            className="inline-flex items-center gap-0.5 text-[11px] text-muted transition hover:text-text disabled:opacity-50"
          >
            {t('collapse')}
            <ChevronUp size={12} aria-hidden />
          </button>
        ) : null}
      </div>

      <div
        className={classNames(
          'rounded-xl border border-dashed p-2 transition',
          dragOver ? 'border-accent/50 bg-accent-soft/35' : 'border-border/80 bg-white/30 dark:bg-slate-950/10',
          disabled && 'opacity-60'
        )}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <div className="scrollbar-thin flex items-center gap-1.5 overflow-x-auto touch-pan-x">
          {value.map((file, index) => {
            const previewUrl = previewUrlsRef.current.get(getReferenceImageKey(file));

            return (
              <div key={getReferenceImageKey(file)} className="relative inline-flex shrink-0">
                {previewUrl ? (
                  <img
                    src={previewUrl}
                    alt={t('chatAttachmentPreviewAlt')}
                    className="h-14 w-14 rounded-lg border border-border/70 object-cover"
                  />
                ) : (
                  <div className="flex h-14 w-14 items-center justify-center rounded-lg border border-border/70 bg-white/60 dark:bg-slate-900/40">
                    <ImagePlus size={16} className="text-muted" />
                  </div>
                )}
                <button
                  type="button"
                  disabled={disabled}
                  onClick={() => removeFile(index)}
                  aria-label={t('removeAttachment')}
                  className="absolute -end-1 -top-1 inline-flex h-4 w-4 items-center justify-center rounded-full border border-white/80 bg-slate-900/75 text-white transition hover:bg-rose-500 disabled:cursor-not-allowed"
                >
                  <X size={9} />
                </button>
              </div>
            );
          })}

          {canAddMore ? (
            <button
              type="button"
              disabled={disabled}
              onClick={openFilePicker}
              className="inline-flex h-14 w-14 shrink-0 flex-col items-center justify-center gap-0.5 rounded-lg border border-border/80 bg-white/60 text-center transition hover:border-accent/35 hover:bg-white dark:bg-slate-900/35 dark:hover:bg-slate-900/60 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <ImagePlus size={15} className="text-accent" />
              <span className="px-0.5 text-[9px] font-medium leading-tight text-muted">{t('aiReferenceImagesAdd')}</span>
            </button>
          ) : null}
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          className="sr-only"
          disabled={disabled || !canAddMore}
          onChange={handleFileChange}
        />
      </div>

      <p className="text-[10px] leading-4 text-muted">
        {t('aiReferenceImagesConstraints', { max: maxCount, sizeMb: REFERENCE_IMAGE_MAX_SIZE_MB })}
      </p>

      {(error || fieldError) ? (
        <p className="text-xs text-rose-600 dark:text-rose-300" role="alert">
          {error || fieldError}
        </p>
      ) : null}
    </div>
  );
};
